import { cn, UserAvatar } from '@frontend-platform/ui-lib';
import {
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@frontend-platform/ui-lib/ui';
import type { AISUser } from '@frontend-platform/user-directory';
import { Ellipsis, Info, Pencil, Trash2 } from 'lucide-react';
import type { CalibrationMetric } from '../../../lib/types';
import { deleteInUseTooltip, METRIC_TYPES, runCountLabel } from '../constants';

interface MetricCardProps {
  metric: CalibrationMetric;
  checked: boolean;
  canManage: boolean;
  onToggle: () => void;
  onRename: (metric: CalibrationMetric) => void;
  onDelete: (metric: CalibrationMetric) => void;
}

function formatUpdatedTooltip(metric: CalibrationMetric): string {
  const updatedBy = metric.updatedBy;
  if (!updatedBy || typeof updatedBy === 'string' || !metric.updatedAt) return '';
  const name = updatedBy.name;
  if (!name) return '';
  const n = Number(metric.updatedAt);
  const d = Number.isFinite(n) ? new Date(n) : new Date(String(metric.updatedAt));
  if (Number.isNaN(d.getTime())) return '';
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  return `Updated by ${name} at ${date}, ${time}`;
}

export function MetricCard({
  metric,
  checked,
  canManage,
  onToggle,
  onRename,
  onDelete,
}: MetricCardProps) {
  const isCustom = metric.type === METRIC_TYPES.CUSTOM;
  const usedInRuns = Number(metric.usedInRuns) || 0;
  const isReferenced = usedInRuns > 0;
  const updatedTooltip = formatUpdatedTooltip(metric);
  const updatedByUser =
    metric.updatedBy && typeof metric.updatedBy !== 'string' ? (metric.updatedBy as AISUser) : null;

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: card mirrors legacy clickable metric tile; checkbox provides the accessible control
    <div
      className={cn(
        'group flex w-full cursor-pointer items-start gap-2 rounded-xl border p-3 transition-colors',
        checked
          ? 'border-input bg-muted shadow-sm'
          : 'border-border hover:border-input hover:bg-muted'
      )}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onToggle();
      }}
    >
      <Checkbox
        checked={checked}
        className="mt-0.5"
        onCheckedChange={onToggle}
        onClick={(e) => e.stopPropagation()}
      />

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1 font-semibold text-foreground text-sm">
            <span className="truncate">{metric.displayName}</span>
            {metric.description ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: stops card toggle; tooltip trigger handles focus */}
                  <span
                    className="inline-flex shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <Info className="h-3 w-3 text-muted-foreground" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs">{metric.description}</TooltipContent>
              </Tooltip>
            ) : null}
          </span>

          {canManage && (
            // biome-ignore lint/a11y/noStaticElementInteractions: stops card toggle when using the menu
            <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex h-5 w-5 items-center justify-center rounded text-foreground hover:bg-border"
                  >
                    <Ellipsis className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel className="text-muted-foreground text-xs">
                    Actions
                  </DropdownMenuLabel>
                  <DropdownMenuItem onSelect={() => onRename(metric)}>
                    <Pencil className="h-4 w-4" />
                    Rename
                  </DropdownMenuItem>
                  {isReferenced ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <DropdownMenuItem disabled>
                            <Trash2 className="h-4 w-4" />
                            Delete (in use)
                          </DropdownMenuItem>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-xs">
                        {deleteInUseTooltip(usedInRuns)}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <DropdownMenuItem
                      className="text-red-600 focus:text-red-600"
                      onSelect={() => onDelete(metric)}
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </span>
          )}
        </div>

        <div className="flex items-center justify-between gap-2">
          <span
            className={cn(
              'inline-flex items-center rounded-full border border-border bg-background px-2 py-0.5 text-xs',
              isReferenced ? 'text-muted-foreground' : 'text-muted-foreground'
            )}
          >
            {runCountLabel(usedInRuns)}
          </span>

          {isCustom && (
            <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 py-0.5 pr-2 pl-1 font-medium text-blue-700 text-xs">
              {updatedByUser ? (
                updatedTooltip ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {/* biome-ignore lint/a11y/noStaticElementInteractions: stops card toggle; tooltip trigger handles focus */}
                      <span
                        className="inline-flex"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        {/* showTooltip={false}: UserAvatar ships its own
                            name/email tooltip, which would render on top of
                            this wrapping "Updated by …" one — two overlapping
                            tooltips for a single hover. The wrapper wins
                            because it carries the timestamp too. */}
                        <UserAvatar
                          name={updatedByUser.name}
                          email={updatedByUser.email}
                          seed={updatedByUser.id || metric.id}
                          size="sm"
                          className="size-4 text-[8px]"
                          showTooltip={false}
                        />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="text-xs">{updatedTooltip}</TooltipContent>
                  </Tooltip>
                ) : (
                  <UserAvatar
                    name={updatedByUser.name}
                    email={updatedByUser.email}
                    seed={updatedByUser.id || metric.id}
                    size="sm"
                    className="size-4 text-[8px]"
                  />
                )
              ) : null}
              Custom
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
